import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  Column,
  ColumnKeys,
  FILTERS,
  getGridColumnDetails,
  GRID_COLUMNS,
  permissionRenderer,
  PREDEFINED_FILTERS_KEYS,
  ruleTypeRenderer,
} from './grid-config';
import { DeviceControlExclusionRuleItem } from '@nirvana/shared/dtos/types/policies/common';
import { GzDeviceControlExclusionsGridDataProvider } from './GzDeviceControlExclusionsGridDataProvider';
import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_DEVICE_CONTROL_EXCLUSIONS_GRID } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { html, PropertyValues, TemplateResult } from 'lit';
import { GzButtonWebComponent } from '../../../../../../../ui/gzButton';
import { ValidationResult } from '../../../../../../../ui/gzForm/formField/types/form-validator';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from '../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { ManualAddExceptionContainerWebComponent } from '../../helpers/manualAddExceptionContainer';
import {
  getDefaultDeviceControlExclusionRuleForm,
  mapToExclusionDiscoveredDevicesRuleForm,
} from '../../../../utils/uiSettings/deviceControl/device-control.values';
import { OverlayPositionX } from '../../../../../../../ui/gzSelect/types';
import { DiscoveredDevicesAddExceptionContainerWebComponent } from '../../helpers/discoveredDevicesAddExceptionContainer';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { ImageState } from '../../../../../../../ui/gzImageRepo/types';
import { ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../ui/gzButton/types';

@customElement(WEB_COMPONENT_POLICY_PANEL_DEVICE_CONTROL_EXCLUSIONS_GRID)
export class GzDeviceControlExclusionsGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  DeviceControlExclusionRuleItem,
  DeviceControlExclusionRuleItem,
  GzDeviceControlExclusionsGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
  protected hasEditRowAction = false;
  protected hasBasicEditRowAction = true;
  private currentlyEditedPosition: number;
  protected showHeaderCustomActionsInsideGrid = true;

  protected translations = {
    editRow: translateFn('Edit'),
    deleteRow: translateFn('Delete'),
    emptyState: translateFn('No exclusions'),
  };

  protected hasSelectableRows = true;
  protected emptyStateImage = ImageState.START;

  protected columnRenderers = {
    [ColumnKeys.RULE_TYPE]: ruleTypeRenderer.bind(this),
    [ColumnKeys.PERMISSION]: permissionRenderer.bind(this),
  };
  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;

  private inlineActions = [
    { title: translateFn('Manually'), value: 'ManualAdd' },
    { title: translateFn('From discovered devices'), value: 'DiscoveredAdd' },
  ];
  protected getDataProvider(): GzDeviceControlExclusionsGridDataProvider {
    return new GzDeviceControlExclusionsGridDataProvider(this.initialGridItems);
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.updateComplete.then(() => {
      const gridToolbar = this.gridComponent.shadowRoot?.querySelector('.grid-header__toolbar') as HTMLElement;
      gridToolbar.style.paddingTop = '0px';
    });
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <div style="display: flex; align-items: center;">
        <gz-select
          id="add-button-select"
          .options="${this.inlineActions}"
          @selectionChanged="${this.onSelectionChanged.bind(this)}"
          overlayPositionX="${OverlayPositionX.LEFT}"
        >
          <gz-button id="add-button" type="${ButtonTypes.OUTLINE}">
            ${translateFn('Add')} <gz-icon name="ds-icon-textbox_decrease" color="primary" size="20"></gz-icon>
          </gz-button>
        </gz-select>

        <gz-button
          id="delete-button"
          type="outline"
          disabled="true"
          color="negative"
          @click="${this.onDeleteSelectedItems.bind(this)}"
          >${translateFn('Delete')}
        </gz-button>
      </div>
    `;
  }

  private async onSelectionChanged(event: CustomEvent) {
    if (event.detail.selected === 'ManualAdd') {
      this.openManualAddDialog();
    } else {
      this.openDiscoveredDevicesAddDialog();
    }
  }

  protected onBasicEditRow(event: CustomEvent) {
    this.openManualAddDialogAsEdit(event.detail.rowData);
  }

  protected onBasicEditRowButton(rowData: any) {
    this.openManualAddDialogAsEdit(rowData);
  }

  private openManualAddDialogAsEdit(rowData: any) {
    const editRulesCallBack = (formValue: DeviceControlExclusionRuleItem[]): ValidationResult => {
      const newItems: DeviceControlExclusionRuleItem[] = formValue;
      const editedItem = newItems[0];
      const addedItems = newItems.slice(1);

      // update given position item
      this.gridComponent.propertiesSet = false;
      (this.gridComponent.dataProvider as GzDeviceControlExclusionsGridDataProvider).updateItem(
        this.currentlyEditedPosition,
        editedItem
      );
      this.gridComponent.propertiesSet = true;

      // add new items
      addedItems.forEach(item => this.onNewGridItemAdd(item));

      return { valid: true };
    };

    const allItems = (this.gridComponent.dataProvider as GzDeviceControlExclusionsGridDataProvider).items;

    allItems.splice(rowData.item.defaultPositionInGrid - 1, 1);
    this.currentlyEditedPosition = rowData.item.defaultPositionInGrid;

    this.openAddDialog(
      new ManualAddExceptionContainerWebComponent(allItems, mapToExclusionDiscoveredDevicesRuleForm(rowData.item)),
      translateFn('Edit exclusion'),
      editRulesCallBack
    );
  }

  private openManualAddDialog() {
    const addRulesCallback = (formValue: DeviceControlExclusionRuleItem[]): ValidationResult => {
      formValue.forEach(item => this.onNewGridItemAdd(item));
      return { valid: true };
    };

    this.openAddDialog(
      new ManualAddExceptionContainerWebComponent(
        (this.gridComponent.dataProvider as GzDeviceControlExclusionsGridDataProvider).items,
        getDefaultDeviceControlExclusionRuleForm()
      ),
      translateFn('Add exclusions'),
      addRulesCallback
    );
  }

  private openDiscoveredDevicesAddDialog() {
    const addRulesCallback = (formValue: DeviceControlExclusionRuleItem[]): ValidationResult => {
      formValue.forEach(item => this.onNewGridItemAdd(item));
      return { valid: true };
    };

    this.openAddDialog(
      new DiscoveredDevicesAddExceptionContainerWebComponent(
        (this.gridComponent.dataProvider as GzDeviceControlExclusionsGridDataProvider).items,
        getDefaultDeviceControlExclusionRuleForm()
      ),
      translateFn('Add exclusions from discovered devices'),
      addRulesCallback
    );
  }

  private openAddDialog(
    formComponent: GzPolicyFullPageWindowFormWebComponent<any>,
    title: string,
    addRulesCallback: (formValue: any) => ValidationResult
  ) {
    const configs: FullPagePopupConfigs = {
      bodyForm: formComponent,
      strings: {
        title: title,
        confirm: translateFn('Add exclusions'),
        cancel: translateFn('Cancel'),
      },
      confirmCallbackFn: addRulesCallback,
    };

    new GzPolicyFullPageWindow(configs).show();
  }

  protected renderEmptyStateGrid(): TemplateResult | null {
    return html`
      <gz-text fontSize="14">${translateFn('Adjust your filters or start adding exclusions.')}</gz-text>
      <gz-select
        id="empty-state-add-button-select"
        style="padding-top: 5px"
        .options="${this.inlineActions}"
        class="mr-2"
        closeOnMouseLeave
        @selectionChanged="${this.onSelectionChanged.bind(this)}"
        overlayPositionX="${OverlayPositionX.LEFT}"
      >
        <gz-button id="empty-state-add-button" type="outline" class="mr-2">${translateFn('Add Exclusions')} </gz-button>
      </gz-select>
    `;
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);
    this.deleteButton.disabled = !event.detail.selectionStoreData.length;
  }
}
